import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PanelManagementFlow from "../components/panels/PanelManagementFlow";
import SelectPanelists from "../components/panels/SelectPanelists";
import CadreDetailsModal from "../components/shared/CadreDetailsModal";
import PanelConflictOfInterestModal from "../components/shared/PanelConflictOfInterestModal";
import ReusableTable from "../components/shared/ReusableTable";
import { useAuth } from "../hooks/useAuth";
import { panelAPI, panelistAPI } from "../services/api";
import { handleRovingTabKeyDown } from "../utils/tabKeyboard";

const PanelDetails = () => {
  const { user, isAuthenticated, isPanelAdmin } = useAuth();
  const { id: panelId } = useParams();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allCadres, setAllCadres] = useState([]);
  const [selectedCadre, setSelectedCadre] = useState(null);
  const [showCadreModal, setShowCadreModal] = useState(false);
  const [needsConflictOfInterest, setNeedsConflictOfInterest] = useState(false);
  const [showCoiModal, setShowCoiModal] = useState(false);

  const fetchPanelDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await panelAPI.getPanelById(panelId);
      if (response.success) {
        setPanel(response.panel);
        // Check if COI is needed for Cadre users
        if (response.needsConflictOfInterest) {
          setNeedsConflictOfInterest(true);
          setShowCoiModal(true);
        }
      } else {
        setError("Panel not found or you don't have permission to view it.");
      }
    } catch (error) {
      console.error("Error fetching panel details:", error);
      if (error?.response?.status === 403) {
        setError(
          error.response.data?.message ||
            "Access denied due to a declared Conflict of Interest."
        );
        navigate("/panels");
        return;
      }
      setError("Failed to load panel details");
    } finally {
      setLoading(false);
    }
  }, [panelId, navigate]);

  const fetchAllCadres = useCallback(async () => {
    try {
      const response = await panelistAPI.getPanelCadres(panelId);
      if (response.success) {
        setAllCadres(response.cadres || []);
      }
    } catch (error) {
      console.error("Error fetching panel cadres:", error);
    }
  }, [panelId]);

  useEffect(() => {
    fetchPanelDetails();
    fetchAllCadres();
  }, [panelId, isAuthenticated, fetchPanelDetails, fetchAllCadres]);

  const handleViewCadreDetails = (cadre) => {
    setSelectedCadre(cadre);
    setShowCadreModal(true);
  };

  const getConflictStatusColor = (status) => {
    switch (status) {
      case "YES":
        return "bg-red-100 text-red-800";
      case "NO":
        return "bg-green-100 text-green-800";
      case "NOT_ANSWERED":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading panel details...</div>
      </div>
    );
  }

  if (error || !panel) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Panel Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            {error ||
              "This panel doesn't exist or you don't have permission to view it."}
          </p>
          <button
            onClick={() => navigate("/panels")}
            className="bg-custom-blue text-white px-6 py-2 rounded-lg hover:bg-custom-light-blue transition-colors"
          >
            Back to Panels
          </button>
        </div>
      </div>
    );
  }

  const isUserPanelAdmin = isPanelAdmin(panel.id);
  const isUserPanelMember = panel.members?.some(
    (member) => member.user?.id === user?.id
  );

  // If COI is required and modal should be shown, prevent access to panel content
  if (needsConflictOfInterest && !panel) {
    return null; // Don't show anything until panel data is loaded
  }

  // Show only COI modal if needed, blocking access to panel content
  if (needsConflictOfInterest && showCoiModal) {
    return (
      <PanelConflictOfInterestModal
        isOpen={showCoiModal}
        onClose={() => {
          // Only close after successful submission (handled in modal)
          setShowCoiModal(false);
          setNeedsConflictOfInterest(false);
        }}
        panelId={panelId}
      />
    );
  }

  return (
    <div>
      <div className="bg-gray-50 min-h-[calc(100vh-200px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {panel.panelName}
                  {panel.visibility === "PRIVATE" && (
                    <span className="ml-3 text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                      Private Panel
                    </span>
                  )}
                </h1>
                <p className="text-gray-600">{panel.panelType}</p>
                <div className="flex items-center space-x-4 mt-4">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      (panel.panelStatus || panel.status) === "Closed"
                        ? "bg-green-100 text-green-800"
                        : (panel.panelStatus || panel.status) === "Active"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {(panel.panelStatus || panel.status || "Active").replace(
                      "_",
                      " "
                    )}
                  </span>
                  <span className="text-sm text-gray-500">
                    Created: {panel.createdAt}
                  </span>
                  <span className="text-sm text-gray-500">
                    Deadline: {panel.deadline}
                  </span>
                  {panel.completedDate && (
                    <span className="text-sm text-green-600">
                      Completed: {panel.completedDate}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                {isUserPanelAdmin &&
                  (panel.panelStatus || panel.status) !== "Closed" && (
                    <button
                      onClick={() => navigate(`/panel-admin/${panel.id}`)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Admin Panel
                    </button>
                  )}
                {isUserPanelMember && (
                  <button className="px-4 py-2 bg-custom-blue text-white rounded-lg hover:bg-custom-light-blue transition-colors">
                    Upload Document
                  </button>
                )}
                <button
                  onClick={() => navigate("/panels")}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back to List
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Overall Progress</span>
                <span className="text-gray-900 font-semibold">
                  {panel.progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${
                    panel.progress === 100
                      ? "bg-green-500"
                      : "bg-custom-light-blue"
                  }`}
                  style={{ width: `${panel.progress}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200">
              <div
                className="flex -mb-px overflow-x-auto"
                role="tablist"
                aria-label="Panel details sections"
              >
                {[
                  "overview",
                  "documents",
                  "timeline",
                  "members",
                  "decisions",
                  "select panelists",
                  "workflow",
                ].map((tab, index, panelTabs) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    id={`panel-details-tab-${tab.replace(/\s+/g, "-")}`}
                    aria-selected={activeTab === tab}
                    aria-controls={`panel-details-panel-${tab.replace(/\s+/g, "-")}`}
                    tabIndex={activeTab === tab ? 0 : -1}
                    onClick={() => setActiveTab(tab)}
                    onKeyDown={(e) =>
                      handleRovingTabKeyDown(
                        e,
                        index,
                        panelTabs,
                        setActiveTab,
                        (t) => `panel-details-tab-${t.replace(/\s+/g, "-")}`
                      )
                    }
                    className={`py-4 px-6 border-b-2 font-medium text-sm capitalize focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                      activeTab === tab
                        ? "border-custom-blue text-custom-blue"
                        : "border-transparent text-gray-700 hover:text-gray-900 hover:border-gray-300"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div
              id={`panel-details-panel-${activeTab.replace(/\s+/g, "-")}`}
              role="tabpanel"
              aria-labelledby={`panel-details-tab-${activeTab.replace(/\s+/g, "-")}`}
              className="p-6"
            >
              {/* Overview Tab */}
              {activeTab === "overview" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Panel Overview
                  </h2>
                  <div className="prose max-w-none">
                    {/* Description removed */}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3">
                          Key Information
                        </h3>
                        <dl className="space-y-2">
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Panel ID:</dt>
                            <dd className="font-medium">#{panel.id}</dd>
                          </div>
                          {/* Community field removed */}
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Panel Members:</dt>
                            <dd className="font-medium">
                              {panel.members?.length || 0}
                            </dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Documents:</dt>
                            <dd className="font-medium">
                              {panel.documents.length}
                            </dd>
                          </div>
                        </dl>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3">
                          Timeline
                        </h3>
                        <dl className="space-y-2">
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Created:</dt>
                            <dd className="font-medium">{panel.createdDate}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Deadline:</dt>
                            <dd className="font-medium">{panel.deadline}</dd>
                          </div>
                          {panel.completedDate && (
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Completed:</dt>
                              <dd className="font-medium text-green-600">
                                {panel.completedDate}
                              </dd>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Days Remaining:</dt>
                            <dd className="font-medium">
                              {(panel.panelStatus || panel.status) === "Closed"
                                ? "N/A"
                                : Math.ceil(
                                    (new Date(panel.deadline) - new Date()) /
                                      (1000 * 60 * 60 * 24)
                                  ) + " days"}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === "documents" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Panel Documents
                  </h2>
                  {panel.documents.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No documents uploaded yet.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {panel.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center">
                            <svg
                              className="w-8 h-8 text-red-500 mr-3"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                            </svg>
                            <div>
                              <p className="font-medium text-gray-900">
                                {doc.name}
                              </p>
                              <p className="text-sm text-gray-500">
                                Uploaded: {doc.uploadDate} • Type: {doc.type}
                              </p>
                            </div>
                          </div>
                          <button className="text-custom-blue hover:text-custom-light-blue font-medium">
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Timeline Tab */}
              {activeTab === "timeline" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Panel Timeline
                  </h2>
                  <div className="space-y-4">
                    <div className="flex">
                      <div className="flex flex-col items-center mr-4">
                        <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                        <div className="w-0.5 h-16 bg-gray-300"></div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Panel Created
                        </p>
                        <p className="text-sm text-gray-500">
                          {panel.createdDate}
                        </p>
                      </div>
                    </div>

                    {panel.documents.map((doc, index) => (
                      <div key={doc.id} className="flex">
                        <div className="flex flex-col items-center mr-4">
                          <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                          <div className="w-0.5 h-16 bg-gray-300"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Document Uploaded
                          </p>
                          <p className="text-sm text-gray-500">
                            {doc.name} • {doc.uploadDate}
                          </p>
                        </div>
                      </div>
                    ))}

                    {(panel.panelStatus || panel.status) === "Closed" ? (
                      <div className="flex">
                        <div className="flex flex-col items-center mr-4">
                          <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Panel Completed
                          </p>
                          <p className="text-sm text-gray-500">
                            {panel.completedDate}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex">
                        <div className="flex flex-col items-center mr-4">
                          <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-400">Deadline</p>
                          <p className="text-sm text-gray-400">
                            {panel.deadline}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Members Tab */}
              {activeTab === "members" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Available CADRE Members
                  </h2>
                  <p className="text-gray-600 mb-6">
                    View CADRE members available for this panel (including those
                    already selected)
                  </p>

                  <ReusableTable
                    columns={[
                      {
                        key: "name",
                        label: "Name",
                        render: (cadre) => (
                          <div>
                            <div className="font-medium text-gray-900">
                              {cadre.name || "Unknown Member"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {cadre.email || "No email"}
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: "conflictOfInterest",
                        label: "Conflict of Interest",
                        render: (cadre) => (
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getConflictStatusColor(
                              cadre.conflictOfInterestDetail?.evaluatedStatus ||
                                cadre.panelConflictOfInterest
                                  ?.evaluatedStatus ||
                                "NOT_ANSWERED"
                            )}`}
                          >
                            {cadre.conflictOfInterestDetail?.evaluatedStatus ||
                              cadre.panelConflictOfInterest?.evaluatedStatus ||
                              "NOT_ANSWERED"}
                          </span>
                        ),
                      },
                      {
                        key: "experience",
                        label: "Experience",
                        render: (cadre) => (
                          <span className="text-sm text-gray-900">
                            {cadre.cadreProfile?.yearsExperience || 0} years
                          </span>
                        ),
                      },
                      {
                        key: "rating",
                        label: "Rating",
                        render: (cadre) => (
                          <span className="text-sm text-gray-900">
                            {cadre.cadreProfile?.rating
                              ? `${cadre.cadreProfile.rating}/5`
                              : "Not rated"}
                          </span>
                        ),
                      },
                      {
                        key: "actions",
                        label: "Actions",
                        render: (cadre) => (
                          <button
                            onClick={() => handleViewCadreDetails(cadre)}
                            className="px-3 py-1 text-sm bg-custom-blue text-white rounded-lg hover:bg-custom-light-blue transition-colors"
                          >
                            View Details
                          </button>
                        ),
                      },
                    ]}
                    data={allCadres}
                    emptyStateMessage="No CADRE members available for this panel"
                    containerClassName="mt-4"
                  />
                </div>
              )}

              {/* Decisions Tab */}
              {activeTab === "decisions" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Panel Decisions
                  </h2>
                  {panel.decisions.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No decisions have been made yet.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {panel.decisions.map((decision) => (
                        <div
                          key={decision.id}
                          className="bg-green-50 border border-green-200 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-green-900">
                              Decision: {decision.decision}
                            </h3>
                            <span className="text-sm text-green-700">
                              {decision.date}
                            </span>
                          </div>
                          <p className="text-green-800">{decision.summary}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Select Panelists Tab */}
              {activeTab === "select panelists" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Select Panelists
                  </h2>
                  <SelectPanelists panel={panel} currentUser={user} />
                </div>
              )}

              {/* Workflow Tab - Panel management workflow */}
              {activeTab === "workflow" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Panel Management Workflow
                  </h2>
                  <PanelManagementFlow />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cadre Details Modal */}
      <CadreDetailsModal
        isOpen={showCadreModal}
        onClose={() => {
          setShowCadreModal(false);
          setSelectedCadre(null);
        }}
        type="cadre"
        data={selectedCadre}
        showSelectButton={false}
      />

      {/* Conflict of Interest Modal */}
      <PanelConflictOfInterestModal
        isOpen={showCoiModal && needsConflictOfInterest}
        onClose={() => {
          // Refetch panel details to check if COI is now completed
          fetchPanelDetails();
          setShowCoiModal(false);
        }}
        panelId={panelId}
      />
    </div>
  );
};

export default PanelDetails;
